import { Calendar, DollarSign, Users } from "lucide-react";

import SurfaceCard from "@/components/primitives/SurfaceCard";
import { SectionHead } from "@/components/organization/kit";

interface GrowthMilestone {
  year: number;
  agents: number;
  volume: string;
}

interface GrowthTrajectoryProps {
  milestones: GrowthMilestone[];
  narrative: string;
}

const GrowthTrajectory = ({ milestones, narrative }: GrowthTrajectoryProps) => {
  const maxAgents = Math.max(...milestones.map((m) => m.agents), 0) || 1;

  return (
    <SurfaceCard padding="lg" className="rounded-2xl">
      <SectionHead kicker="Growth trajectory" title="Year over year" aside={<span className="text-xs text-ink-500">{narrative}</span>} />

      <div className="relative">
        {/* Timeline rail */}
        <div className="absolute bottom-0 left-8 top-0 w-0.5 bg-border" />

        <div className="space-y-8">
          {milestones.map((milestone, index) => {
            const isLast = index === milestones.length - 1;
            const barWidth = (milestone.agents / maxAgents) * 100;

            return (
              <div key={milestone.year} className="relative flex items-start gap-6">
                {/* Year marker */}
                <div
                  className={`relative z-10 flex h-16 w-16 flex-col items-center justify-center rounded-full text-sm font-bold tabular-nums ${
                    isLast ? "bg-primary text-primary-foreground" : "bg-primary-tint text-primary-pressed"
                  }`}
                >
                  <Calendar className="mb-0.5 h-4 w-4" />
                  {milestone.year}
                </div>

                {/* Stats */}
                <div className="flex-1 rounded-[var(--radius-md)] border border-border bg-background p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="font-semibold tabular-nums text-ink-900">{milestone.agents} agents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-ink-500" />
                        <span className="font-semibold tabular-nums text-ink-900">{milestone.volume}</span>
                      </div>
                    </div>
                    {isLast && (
                      <span className="rounded-full bg-primary-tint px-2 py-1 text-xs font-medium text-primary-pressed">
                        Current
                      </span>
                    )}
                  </div>

                  {/* Growth bar */}
                  <div className="h-3 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${barWidth}%`,
                        background: isLast ? "hsl(var(--chart-seq-2))" : "hsl(var(--chart-seq-3))",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SurfaceCard>
  );
};

export default GrowthTrajectory;
